import { focusFirstInvalidField } from '$core/helpers/focus-first-invaild-field';
import { handleFormHttpError } from '$core/helpers/server-errors-form-handler';
import { SwalService } from '$core/shell/swal/swal.service';
import { Card } from '$modules/personal/customer-card/models/card';
import { CorporateCardService } from '$modules/personal/customer-card/services/corporate-card.service';
import { transition, trigger, useAnimation } from '@angular/animations';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { fadeIn } from 'ng-animate';
import { EMPTY } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import {
  DIRECTIONS,
  DOCUMENTS,
  NEGOTIATION_RATE,
  PAYMENT_TYPE,
  PaymentType,
  POSTAL_TYPE,
  PostalType,
  RECIVING,
  resetDirections,
  resetPostalTypes,
} from '../../metadata';
import { BatchesParseStorageService, BatchesService } from '../../services';
import { untilDestroyed } from '$core/operators/until-destroyed.operator';
import { triggerFormValidation } from '$core/helpers/trigger-form-validation';
import { Batch } from '$modules/personal/modules/batch/features/batches/models';
import { validateLocalStoragePerPage } from '$modules/personal/modules/batch/validateLocalStorageBatch';

@Component({
  selector: 'app-create-batch',
  templateUrl: './create-batch.component.html',
  styleUrls: ['./create-batch.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      transition(':enter', useAnimation(fadeIn), {
        params: { timing: 0.2 },
      }),
    ]),
  ],
})
export class CreateBatchComponent implements OnInit, OnDestroy {
  @ViewChild('formEl', { static: false })
  public formEl: ElementRef<HTMLFormElement>;

  public readonly DIRECTIONS = DIRECTIONS;
  public POSTAL_TYPE = POSTAL_TYPE;
  public readonly NEGOTIATION_RATE = NEGOTIATION_RATE;
  public readonly DOCUMENTS = DOCUMENTS;
  public readonly RECIVING = RECIVING;

  public PAYMENT_TYPE = PAYMENT_TYPE.slice();
  public form: FormGroup;
  public cards: Card[];
  public isLoading = false;
  public isLoadingAccounts = false;
  public showDeclaredValue: boolean;

  // Экспресс-посылка (ранее EMS)
  public get isEms(): boolean {
    return this.form.get('postal_delivery_type').value === 'ems';
  }

  // Мелкий пакет с объявленной ценностью республиканский
  public get isSmallPackageDeclareValue(): boolean {
    return (
      this.form.get('postal_delivery_type').value ===
      'small_package_declare_value'
    );
  }

  // Письмо с объявленной ценностью республиканское(удалено)
  public get isLetterDeclaredValue(): boolean {
    return (
      this.form.get('postal_delivery_type').value === 'letter_declare_value'
    );
  }

  // Простая посылка республиканская (без ОЦ)
  public get isPackage(): boolean {
    return this.form.get('postal_delivery_type').value === 'package';
  }

  // Посылка с объявленной ценностью республиканская
  public get isPackageDeclaredValue(): boolean {
    return (
      this.form.get('postal_delivery_type').value === 'package_declare_value'
    );
  }

  public get isEcommerce(): boolean {
    return (
      this.form.get('postal_delivery_type').value === 'ecommerce_economical' ||
      this.form.get('postal_delivery_type').value === 'ecommerce_elite' ||
      this.form.get('postal_delivery_type').value === 'ecommerce_express' ||
      this.form.get('postal_delivery_type').value === 'ecommerce_light' ||
      this.form.get('postal_delivery_type').value === 'ecommerce_optima' ||
      this.form.get('postal_delivery_type').value === 'ecommerce_standard'
    );
  }

  public get isDefaultDeclaredValueTypes() {
    return (
      this.isLetterDeclaredValue ||
      this.isSmallPackageDeclareValue ||
      this.isPackageDeclaredValue
    );
  }

  // Вид оплаты ЭЛС
  public get isELS() {
    return (
      this.form.get('payment_type').value === 'electronic_personal_account'
    );
  }

  // Вид оплаты Гарантийное письмо
  public get isCommitmentLetter() {
    return this.form.get('payment_type').value === 'commitment_letter';
  }

  // Доступные Ecommerce посылки для получения части вложения
  public get isPartialReceiptAvailable() {
    return (
      this.form.get('postal_delivery_type').value === 'economical' ||
      this.form.get('postal_delivery_type').value === 'ecommerce_standard' ||
      this.form.get('postal_delivery_type').value === 'ecommerce_elite' ||
      this.form.get('postal_delivery_type').value === 'ecommerce_express'
    );
  }

  public get isPartialReceiptFormControl() {
    return this.form.get('is_partial_receipt') as FormControl;
  }

  public get isDeclaredValueFormControl() {
    return this.form.get('is_declared_value') as FormControl;
  }

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _batchesService: BatchesService,
    private readonly _cd: ChangeDetectorRef,
    private readonly _translateService: TranslateService,
    private readonly _swalService: SwalService,
    private readonly _router: Router,
    private readonly _corporateCardService: CorporateCardService,
    private readonly _batchStorageService: BatchesParseStorageService
  ) {}

  public ngOnDestroy(): void {
    resetDirections();
    resetPostalTypes();
  }

  public ngOnInit() {
    this.POSTAL_TYPE = this.POSTAL_TYPE.filter(
      (postalType) => postalType.value !== 'letter_declare_value'
    );
    // Отфильтрованы данные в выпадающем списке, чтобы не было возможности создать
    // письмо с объявленной ценностью республиканское
    // Требования почты по унификации. Не менял общие данные POSTAL_TYPE, так как с базы данных
    // letter_declare_value удалять не будут и надо иметь возможность отображать ранее созданные отправления
    this.initForm();
    this.handleDeclaredValueCheckbox();

    if (this._batchStorageService.batchItems.invalid.length) {
      this.showBatchValidationModal();
    }
  }

  private showBatchValidationModal() {
    const swal = this._swalService.swal;
    swal.title = this._translateService.instant(
      'BATCH_MAILING.BATCHES_TAB.VIEWING_BATCH.INCORRECT_EXEL_FILE'
    );
    swal.icon = 'error';
    swal.text = `${this._translateService.instant('UPLOADED')}
      ${this._batchStorageService.batchItems.valid.length} из
      ${
        this._batchStorageService.batchItems.invalid.length +
        this._batchStorageService.batchItems.valid.length
      }.
      ${this._translateService.instant(
        'BATCH_MAILING.BATCHES_TAB.VIEWING_BATCH.EXEL_MODAL_TEXT'
      )}`;
    return swal.fire();
  }

  public submitForm(): void {
    if (this.form.invalid && !this.isLoading) {
      triggerFormValidation(this.form);
      focusFirstInvalidField(this.formEl);
      return;
    }

    this.isLoading = true;
    const body = {
      ...this.form.getRawValue(),
      direction: 'internal',
    };

    if (body.hasOwnProperty('postal_items_in_ops')) {
      body.postal_items_in_ops = Number(body.postal_items_in_ops);
    }

    if (body.hasOwnProperty('is_document')) {
      body.is_document = Number(body.is_document);
    }

    this._batchesService
      .create(body)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this._cd.detectChanges();
        }),
        catchError((err: HttpErrorResponse) => {
          const swal = this._swalService.swal;
          swal.title = this._translateService.instant(
            'BATCH_MAILING.BATCHES_TAB.CREATION.CREATION_ERROR'
          );
          if (err.error.message) {
            swal.text = err.error.message;
          }
          swal.icon = 'error';
          swal.fire().then(() => {
            this.isLoadingAccounts = false;
            this._cd.detectChanges();
          });

          return EMPTY;
        })
      )
      .subscribe(
        (res: Batch) => {
          let perPage = 15;

          if (localStorage.getItem('batch-departures-per-page')) {
            perPage = validateLocalStoragePerPage('batch-departures-per-page');
          }

          return this._router.navigate(
            [`/personal/batch/batches/${res.id}/view-batch`],
            {
              queryParams: {
                perPage,
              },
            }
          );
        },
        (error) => {
          this.handleError(error as HttpErrorResponse, this.form, this.formEl);
        }
      );
  }

  public showAdditionalCriteria(): boolean {
    const allowedPackages =
      this.isPackage ||
      this.isEms ||
      this.isEcommerce ||
      this.isDefaultDeclaredValueTypes;

    const allowedPaymentTypes = this.isELS || this.isCommitmentLetter;

    return allowedPackages && allowedPaymentTypes;
  }

  private initForm(): void {
    this.form = this._fb.group({
      direction: [
        {
          value: 'internal',
          disabled: true,
        },
        Validators.required,
      ], // Направление ПО
      payment_type: [PAYMENT_TYPE[0].value, Validators.required], // Вид оплаты
      postal_delivery_type: [POSTAL_TYPE[0].value, Validators.required], // Вид отправления
      negotiated_rate: [this.NEGOTIATION_RATE[0].value, Validators.required], // Дополнительные критерии
      is_declared_value: [{ value: false, disabled: true }], // Объявленная ценность
      is_partial_receipt: [{ value: false, disabled: false }], // Получение части вложений
      is_priority: [0],
    });

    this.form
      .get('is_partial_receipt')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.handlePartialReceiptAndDeclaredValue();
      });

    this.form
      .get('postal_delivery_type')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((res: PostalType) => {
        this.handleDeclaredValueCheckbox();
        this.isPartialReceiptFormControl.patchValue(false);

        // Тип вложения только для емс
        res === 'ems'
          ? this.form.addControl(
              'is_document',
              this._fb.control(DOCUMENTS[0].value, Validators.required)
            )
          : this.form.removeControl('is_document');

        // Забор ПО только для всех Е-коммерс
        const isPostalInOps =
          res === 'ecommerce_express' ||
          res === 'ecommerce_elite' ||
          res === 'ecommerce_standard';
        isPostalInOps
          ? this.form.addControl(
              'postal_items_in_ops',
              this._fb.control(1, Validators.required)
            )
          : this.form.removeControl('postal_items_in_ops');
      });

    this.form
      .get('payment_type')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((res: PaymentType) => {
        if (res === 'electronic_personal_account') {
          this.getAccounts();
        } else {
          this.form.removeControl('card_number');
        }

        this.handleDeclaredValueCheckbox();
        this.handlePartialReceiptAndDeclaredValue();
        this.isPartialReceiptFormControl.patchValue(false);
        this.form.get('negotiated_rate').patchValue('0', { emitValue: false });
      });

    this.form
      .get('negotiated_rate')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe(() => {
        this.handleDeclaredValueCheckbox();
      });
  }

  private handleDeclaredValueCheckbox(): void {
    if (
      this.isDefaultDeclaredValueTypes &&
      this.form.get('negotiated_rate').value === '1' &&
      (this.isELS || this.isCommitmentLetter)
    ) {
      this.showDeclaredValue = false;
      this.form.get('is_declared_value').patchValue(false);
      return;
    }

    if (this.isDefaultDeclaredValueTypes) {
      this.form.get('is_declared_value').disable();
      this.showDeclaredValue = true;
      this.form.get('is_declared_value').patchValue(true);
    } else if (this.isEcommerce || this.isEms) {
      if (this.form.get('negotiated_rate').value === '1') {
        this.showDeclaredValue = false;
      }
      if (this.form.get('negotiated_rate').value === '0') {
        this.showDeclaredValue = true;
      }

      this.form.get('is_declared_value').patchValue(false);
      this.form.get('is_declared_value').enable();
    } else {
      this.showDeclaredValue = false;
      this.form.get('is_declared_value').patchValue(false);
      this.form.get('is_declared_value').enable();
    }
  }

  private handleError(
    res: HttpErrorResponse,
    form?: FormGroup,
    formEl?: ElementRef<HTMLFormElement>
  ) {
    if (res.status === 422) {
      handleFormHttpError(res, form);
      focusFirstInvalidField(formEl);
    } else {
      throw res;
    }
  }

  private getAccounts(): void {
    this.isLoadingAccounts = true;
    this._corporateCardService
      .getAccounts()
      .pipe(
        catchError(() => {
          const elsIndex = this.PAYMENT_TYPE.findIndex(
            (el) => el.value === 'electronic_personal_account'
          );

          if (elsIndex !== -1) {
            this.PAYMENT_TYPE.splice(elsIndex, 1);
            this.form
              .get('payment_type')
              .setValue(this.PAYMENT_TYPE[0].value, { emitEvent: false });
          }

          const swal = this._swalService.swal;
          swal.title = this._translateService.instant(
            'BATCH_MAILING.BATCHES_TAB.CREATION.CARDS_TITLE'
          );
          swal.html = this._translateService.instant(
            'BATCH_MAILING.BATCHES_TAB.CREATION.CARDS_TEXT'
          );
          swal.icon = 'info';
          swal.fire().then(() => {
            this.isLoadingAccounts = false;
            this._cd.detectChanges();
          });

          return EMPTY;
        }),
        untilDestroyed(this)
      )
      .subscribe((res) => {
        this.cards = res.cards;
        this.isLoadingAccounts = false;
        this.form.addControl(
          'card_number',
          this._fb.control(this.cards[0].number, Validators.required)
        );
        this._cd.detectChanges();
      });
  }

  private handlePartialReceiptAndDeclaredValue() {
    if (!this.isPartialReceiptAvailable) {
      return;
    }

    if (this.isELS || this.isCommitmentLetter) {
      this.handleElsOrCommitmentLetter();
    } else {
      this.handleNonElsNonCommitmentLetter();
    }
  }

  private handleElsOrCommitmentLetter() {
    const negotiatedRate = this.form.get('negotiated_rate').value;

    if (negotiatedRate === '0') {
      this.toggleDeclaredValueControl(this.isPartialReceiptFormControl.value);
    } else if (negotiatedRate === '1') {
      this.showDeclaredValue = false;
      this.isDeclaredValueFormControl.patchValue(false);
    }
  }

  private handleNonElsNonCommitmentLetter() {
    this.toggleDeclaredValueControl(this.isPartialReceiptFormControl.value);
  }

  private toggleDeclaredValueControl(isPartialReceipt: boolean) {
    if (isPartialReceipt) {
      this.isDeclaredValueFormControl.disable();
      this.isDeclaredValueFormControl.patchValue(true);
    } else {
      this.isDeclaredValueFormControl.enable();
      this.isDeclaredValueFormControl.patchValue(false);
    }
  }
}
